/**
 * @file document-content-table.component.ts
 * @module DocumentContentTableComponent
 *
 * @description
 * Displays the line-item content of a single document selected by `selectedDocumentId`.
 *
 * ## Data flow
 * 1. Parent binds `selectedDocumentId` → `ngOnChanges` pushes the value into `docItemList$`.
 * 2. `docItemList$` pipeline debounces, fetches the document header via `getDocListEntry`,
 *    then delegates enrichment to `loadDocContent`.
 * 3. `loadDocContent` issues two parallel HTTP calls via `forkJoin`:
 *    - `getDocItemsByDoc` — always; raw line items.
 *    - `getDocItemsStockAndShipped` — stock-relevant doc types only; single DB round-trip
 *      returning `{ DOCITEMID, PRODID, PRODSTOCK, DOCITEMSHIPPED }[]`.
 *    A single `map` merges the summary into each row, computes totals, and sets the
 *    visual-state flags `FSH` / `LST` / `RDY` on each `DocItemsEx` row.
 *
 * ## Doc-type filtering
 * | Stage                         | Included types  | Reason                                      |
 * |-------------------------------|-----------------|---------------------------------------------|
 * | Totals accumulation           | all             | Every doc type has meaningful qty/price     |
 * | `getDocItemsStockAndShipped`  | 1,2,3,4,5,10,12 | Skip stock queries for non-stock types      |
 *
 * ## Visual-state flags (`DocItemsEx`)
 * | Flag  | Meaning            | Condition                                              |
 * |-------|--------------------|--------------------------------------------------------|
 * | `FSH` | Fully shipped      | type=1 and `DOCITEMSHIPPED === DOCITEMQTY`             |
 * | `LST` | Lack of stock      | type=1 and available stock < remaining qty             |
 * | `RDY` | Ready for shipment | type=1 or 12 and available stock >= remaining qty      |
 */
import { Component, OnDestroy, OnInit, ViewEncapsulation, OnChanges, SimpleChanges, Input, ViewChild } from '@angular/core';
import { map, tap, of, from, forkJoin, EMPTY, Subject } from 'rxjs';
import { switchMap, concatMap, finalize, debounceTime, filter, catchError, takeUntil, take } from 'rxjs/operators';
import { FilterService } from 'primeng/api';
import { Table } from 'primeng/table';

import { environment } from 'src/environments/environment';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { DocListEntry } from 'src/app/shared/doc-list-entry';
import { DocItems } from 'src/app/shared/doc-items';
import { DocumentApiService } from 'src/app/services/document-api.service';


// Extended by flag used in template to color the number to help understand the state of document
// Truly has the meaning only for proforma invoice type (DOCTYPEID=1) and commercial offer (DOCTYPEID=12)
interface DocItemsEx extends DocItems {

  FSH?: boolean;     // Fully shipped
  LST?: boolean;     // Lack on Stock
  RDY?: boolean;     // Ready for shipment
};

@Component({
  selector: 'app-document-content-table',
  templateUrl: './document-content-table.component.html',
  styleUrls: ['./document-content-table.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class DocumentContentTableComponent implements OnInit, OnDestroy, OnChanges {

  private globalSettinsData: GlobalSettingsData = null;

  /** @internal Completes the `takeUntil` guard when the component is destroyed. */
  private destroy$ = new Subject<void>();

  /** Whether the table is currently loading data. Bound to the loading spinner in the template. */
  loading: boolean = false;

  /**
   * DOCTYPEID of the currently displayed document.
   * Written by the pipeline for template binding (`[ngSwitch]="selectedDocType"`).
   * The pipeline's internal logic reads `docrow.DOCTYPEID` directly and does not depend on this field.
   */
  selectedDocType: number = null;

  /** Enriched line items for the currently displayed document. Bound to the template table. */
  selectedDocContent: DocItemsEx[] = null;

  /** Currently selected row in the template table — used as a PrimeNG row-selection placeholder. */
  selectedContentRow: DocItemsEx = null;  // Used only in template table as placeholder for selected row

  /** @internal Subject that drives the main data pipeline; emits a DOCID on every input change. */
  private docItemList$ = new Subject<number>();

  /** DOCID of the document whose content should be displayed. Set by the parent component. */
  @Input() selectedDocumentId: number = 0;

  /** When `true`, renders a footer row with the sum and item-count totals. */
  @Input() showFooterTotal: boolean = false;

  cf: any = [];

  /** @internal PrimeNG Table reference used for programmatic filter/sort resets if needed. */
  @ViewChild('cTable') cTable: Table;

  /** Running total of `DOCITEMSUM` across all line items; displayed in the footer when `showFooterTotal` is `true`. */
  totalInvoiceSum: number = 0;

  /** Running total of `DOCITEMQTY` across all line items; displayed in the footer when `showFooterTotal` is `true`. */
  totalInvoiceItems: number = 0;

  constructor(
    private docApi: DocumentApiService,
    private filterService: FilterService,
    private globalsettingsservice: GetglobalsettingsService,
  ) { }

  /** Completes `destroy$` to unsubscribe all `takeUntil`-guarded pipelines. */
  ngOnDestroy(): void {
    console.log('*** DocumentContentTableComponent ngOnDestroy: ')
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Forwards `selectedDocumentId` changes into the `docItemList$` pipeline. */
  ngOnChanges(changes: SimpleChanges): void {
    console.log('*** DocumentContentTableComponent ngOnChanges: ')
    for (let propName in changes) {
      let change = changes[propName];

      console.log('*** DocumentContentTableComponent ngOnChanges ' + propName + ' (actual): ' + JSON.stringify(change.currentValue));

      switch (propName) {
        case 'selectedDocumentId':
          this.docItemList$.next(change.currentValue);
          break;
      }
    }
  }

  /**
   * Registers custom PrimeNG filter matchers and wires up the `docItemList$` pipeline.
   *
   * Custom filters registered:
   * - `gtOrZero`  — passes when filter is 0 and value is 0, or filter > 0 and value > filter
   * - `GteOrZero` — passes when filter is 0 and value is 0, or filter > 0 and value >= filter
   */
  ngOnInit(): void {
    console.log('*** DocumentContentTableComponent ngOnInit: ')

    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    this.filterService.register('gtOrZero', (value: number, filter: number) => (
      ((filter === 0) && (value === 0)) || ((filter > 0) && (value > filter))
    )
    );

    this.filterService.register('GteOrZero', (value: number, filter: number) => (
      ((filter === 0) && (value === 0)) || ((filter > 0) && (value >= filter))
    )
    );

    this.docItemList$.pipe(

      takeUntil(this.destroy$),
      debounceTime(100),

      tap(docId => {
        this.loading = true;
        environment.production || console.log('*** DocumentContentTableComponent docItemList$ subscribing....: ' + docId);
      }),

      switchMap(docid => this.docApi.getDocListEntry(docid).pipe(take(1))),

      filter(doclist => doclist && doclist.length > 0),
      concatMap(doclist => from(doclist)),

      tap((docrow: DocListEntry) => {
        environment.production || console.log('*** DocumentContentTableComponent docItemList$ subscription DOCTYPE: ' + docrow.DOCTYPEID);

        this.selectedDocType = docrow.DOCTYPEID;
        this.totalInvoiceItems = 0;
        this.totalInvoiceSum = 0;
      }),

      // NOTE #6: selectedDocType is still assigned here for template binding only.
      // The pipeline's internal logic uses docrow.DOCTYPEID directly via the loadDocContent closure.
      concatMap((docrow: DocListEntry) => this.loadDocContent(docrow)),
    )
    .subscribe({
      error: (err) => {
        console.error('*** DocumentContentTableComponent pipeline error:', err);
        this.loading = false;
      }
    });
    this.docItemList$.next(this.selectedDocumentId);
  }

  /**
   * Builds the per-document enrichment pipeline for a single document header row.
   *
   * Uses `forkJoin` to fire two HTTP requests in parallel:
   * - `getDocItemsByDoc`           — always; returns the raw line items.
   * - `getDocItemsStockAndShipped` — only for stock-relevant doc types (1,2,3,4,5,10,12);
   *   returns `{ DOCITEMID, PRODID, PRODSTOCK, DOCITEMSHIPPED }[]` in one DB round-trip,
   *   replacing the previous per-row `getProdInfoById` + `getDocItemsShippedByDoc` chain.
   *
   * After both calls resolve, a single `map` merges the summary into each `DocItemsEx` row,
   * computes totals, and sets `FSH` / `LST` / `RDY` visual-state flags.
   *
   * Errors are caught internally; the outer pipeline stream is kept alive by returning `EMPTY`.
   *
   * @param docrow - The document header row returned by `getDocListEntry`.
   * @returns An Observable that emits the enriched `DocItemsEx[]` array then completes.
   */
  private loadDocContent(docrow: DocListEntry) {
    const needsStock = [1, 2, 3, 4, 5, 10, 12].includes(docrow.DOCTYPEID);
    const gs = this.globalSettinsData;
    const s1 = gs.mainStockId;
    const s2 = gs.impCountFlag ? gs.impStockId : 0;
    const s3 = gs.auxCountFlag ? gs.auxStockId : 0;

    return forkJoin({
      items:   this.docApi.getDocItemsByDoc(docrow.DOCID),
      summary: needsStock
        ? this.docApi.getDocItemsStockAndShipped(docrow.DOCID, gs.mainCompanyId, s1, s2, s3, new Date())
        : of([])
    }).pipe(
      map(({ items, summary }) => {
        const sm = new Map(summary.map(s => [s.DOCITEMID, s]));
        return (items as DocItemsEx[]).map(di => {
          di.DOCITEMSUM  = di.DOCITEMQTY * di.DOCITEMPRICE;
          di.DOCITEMVSUM = di.DOCITEMQTY * di.DOCITEMVPRICE;
          const s = sm.get(di.DOCITEMID);
          di.prodInfo       = s ? { PRODSTOCK: s.PRODSTOCK } as any : undefined;
          di.DOCITEMSHIPPED = s ? s.DOCITEMSHIPPED : 0;
          return di;
        });
      }),
      tap(items => {
        this.selectedDocContent  = items;
        this.totalInvoiceSum     = items.reduce((a, di) => a + (di.DOCITEMSUM  ?? 0), 0);
        this.totalInvoiceItems   = items.reduce((a, di) => a + (di.DOCITEMQTY  ?? 0), 0);
        items.forEach(di => {
          di.FSH = (docrow.DOCTYPEID === 1) && (di.DOCITEMSHIPPED === di.DOCITEMQTY);
          di.LST = (docrow.DOCTYPEID === 1) && !!di.prodInfo &&
                   (di.DOCITEMSHIPPED < di.DOCITEMQTY) &&
                   (di.prodInfo.PRODSTOCK < (di.DOCITEMQTY - di.DOCITEMSHIPPED));
          di.RDY = ([1, 12].includes(docrow.DOCTYPEID)) && !!di.prodInfo &&
                   (di.DOCITEMSHIPPED < di.DOCITEMQTY) &&
                   (di.prodInfo.PRODSTOCK >= (di.DOCITEMQTY - di.DOCITEMSHIPPED));
        });
      }),
      finalize(() => { this.loading = false; }),
      catchError(err => {
        console.error('*** DocumentContentTableComponent loadDocContent error:', err);
        this.loading = false;
        return EMPTY;
      })
    );
  }

}
